import { useEffect, useState } from "react"

/* What the player can actually do — as opposed to how much room they have to do it in.

   `viewport.js` answers "how wide is the window" and answers it deliberately without asking what
   kind of device this is. That is right for layout and wrong for everything else, because the two
   are not the same question and they disagree constantly:

     a 1024px tablet in landscape   -> the desktop-ish shell, and no keys to press
     a 700px window on a PC          -> the phone shell, and a full keyboard beside it
     a 2-in-1 with its keyboard off  -> unchanged width, and the shortcuts stopped working

   So a screen that offers to rebind keyboard shortcuts is not a question about width. It is a
   question about whether a keyboard exists, and the answer has to be asked separately.

   Three capabilities, each from the media feature that actually describes it:

   - `pointer`  — `(pointer: coarse)` is the *primary* input being a finger. A mouse-driven PC is
     "fine" even with a touchscreen attached, which is what we want: it says how the player is most
     likely to aim, not what hardware is present.
   - `hover`    — `(hover: hover)` is whether resting the pointer somewhere can reveal anything. On
     touch it cannot, so anything a design puts behind a hover is simply unreachable and needs
     another door.
   - `keyboard` — no media feature reports one, so this is the honest guess plus an observation.

   The guess is `(any-pointer: fine)`: a device with a mouse or a trackpad almost always has keys
   next to it. The observation is a keydown that a virtual keyboard could not have produced —
   Escape, Tab, an arrow, a function key, or anything held with ctrl/meta/alt. A phone player
   typing in chat fires keydown events all day, so "a key was pressed" on its own proves nothing;
   these are the keys the on-screen keyboards do not have.

   The observation only ever turns it on, never off. A keyboard that has been used exists, and the
   cost of that asymmetry is small and nameable: detach the keyboard from a 2-in-1 mid-session and
   the shortcut settings stay until the page is reloaded. The opposite error — hiding a feature
   from somebody whose keyboard works — is the worse one. */

export const COARSE_POINTER_QUERY = "(pointer: coarse)"
export const FINE_POINTER_QUERY = "(any-pointer: fine)"
export const HOVER_QUERY = "(hover: hover)"

// Keys an on-screen keyboard does not offer. A physical one produces these; a thumb cannot.
const PHYSICAL_ONLY_KEYS = new Set([
  "Escape",
  "Tab",
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "Home",
  "End",
  "PageUp",
  "PageDown",
  "Insert",
])

export const looksLikeAPhysicalKey = (event) =>
  Boolean(event) &&
  (event.ctrlKey ||
    event.metaKey ||
    event.altKey ||
    PHYSICAL_ONLY_KEYS.has(event.key) ||
    /^F\d{1,2}$/.test(event.key || ""))

const asks = (query) => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(query).matches

/* jsdom has no matchMedia, and neither does a server render. Both answer the way a desktop would —
   fine pointer, hover, keyboard — because that is what the app assumed before any of this existed,
   so a test that does not opt in sees exactly what it always saw. */
const readCapabilities = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return { pointer: "fine", hover: true, keyboard: true }
  }
  return {
    pointer: asks(COARSE_POINTER_QUERY) ? "coarse" : "fine",
    hover: asks(HOVER_QUERY),
    keyboard: asks(FINE_POINTER_QUERY),
  }
}

export const useInputCapabilities = () => {
  const [capabilities, setCapabilities] = useState(readCapabilities)
  // Sticky, and separate from the media-query answer so a re-read cannot forget what was seen.
  const [sawAKeyboard, setSawAKeyboard] = useState(false)

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined

    const queries = [COARSE_POINTER_QUERY, HOVER_QUERY, FINE_POINTER_QUERY].map((q) => window.matchMedia(q))
    const onChange = () => setCapabilities(readCapabilities())
    queries.forEach((query) => query.addEventListener("change", onChange))
    onChange()

    return () => queries.forEach((query) => query.removeEventListener("change", onChange))
  }, [])

  useEffect(() => {
    if (sawAKeyboard) return undefined
    const onKeyDown = (event) => {
      if (looksLikeAPhysicalKey(event)) setSawAKeyboard(true)
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [sawAKeyboard])

  return { ...capabilities, keyboard: capabilities.keyboard || sawAKeyboard }
}

export const useHasKeyboard = () => useInputCapabilities().keyboard
export const useCanHover = () => useInputCapabilities().hover

/* The word for "press this thing", as an i18next **context**.
 *
 * Eleven strings across the lobby, the map, the city sheet and the march order told the player to
 * *click* — the map to place a cell, the map to draw a route, a unit to move it up its zone, a
 * building to inspect it. On a phone every one of them names an action the player cannot perform,
 * which is the sort of thing that makes a ported app read as ported.
 *
 * A context rather than an interpolated verb, because the sentence changes with it and not just
 * one word: Spanish goes from "Haz clic en el mapa" to "Toca el mapa", and English from "Click a
 * unit" to "Tap a unit". i18next falls back to the base key when no `_touch` variant exists, so
 * adding one is adding two lines to the dictionaries and nothing else.
 *
 * This is `input`'s question and not `viewport`'s: a 700px desktop window still has a mouse, and a
 * 1024px tablet does not. */
export const usePointerContext = () => (useInputCapabilities().pointer === "coarse" ? "touch" : undefined)

/* Published on the document element beside `data-shell`, for the same reason: a stylesheet needs
   to know, and the answer is decided in one place.

   `@media (pointer: coarse)` would cover most of it and gets one thing wrong that matters — the
   keycaps under the map actions. Those name keys, so they should follow the *keyboard*, and a
   tablet with a keyboard paired to it is a coarse pointer that can still press M. Asking the wrong
   media feature would hide a legend for shortcuts that work. */
export const useInputAttributes = () => {
  const capabilities = useInputCapabilities()

  useEffect(() => {
    const root = document.documentElement
    root.dataset.pointer = capabilities.pointer
    root.dataset.keyboard = capabilities.keyboard ? "yes" : "no"
    return () => {
      delete root.dataset.pointer
      delete root.dataset.keyboard
    }
  }, [capabilities.pointer, capabilities.keyboard])

  return capabilities
}
